import { useQuery } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { dashboardApi } from '@/services/api';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/** How much of the free database this app uses, and the 1.x archive tables until they expire. */
export function StorageSection() {
  const { data, isLoading, error } = useQuery({ queryKey: ['storage'], queryFn: () => dashboardApi.storage() });
  const report = data?.data;
  const percent = report ? (report.usedBytes / report.limitBytes) * 100 : 0;

  return (
    <section className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-3">
        <Database className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-semibold">Storage</h2>
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">Could not load storage use.</p>}
      {report && (
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex justify-between text-sm">
              <span>Database</span>
              <span className="text-muted-foreground">
                {formatBytes(report.usedBytes)} used of the {formatBytes(report.limitBytes)} free tier ({percent < 0.1 ? '<0.1' : percent.toFixed(1)}%)
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted">
              <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max(percent, 0.5)}%` }} />
            </div>
          </div>
          {report.archives.length > 0 ? (
            <div>
              <p className="mb-2 text-sm">
                1.x archive (from Tripper) <span className="text-muted-foreground">(read-only copy, deleted automatically on {report.archiveExpiresOn})</span>
              </p>
              <table className="w-full text-sm">
                <tbody>
                  {report.archives.map((a) => (
                    <tr key={a.table} className="border-t border-border">
                      <td className="py-1.5">{a.table.replace('_v1_archive', '').replace('_', ' ')}</td>
                      <td className="py-1.5 text-right text-muted-foreground">{a.rows.toLocaleString()} rows</td>
                      <td className="py-1.5 text-right text-muted-foreground">{formatBytes(a.bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">The 1.x archive (from Tripper) has been deleted.</p>
          )}
        </div>
      )}
    </section>
  );
}
